import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuCheck, LuExternalLink } from "react-icons/lu";

import CuttingPickerCard, {
  CardQtyRow,
  CardRateRow,
  rowClass,
  rowLabelClass,
} from "../cuttingLines/CuttingPickerCard";
import CuttingPickerDialog, {
  PickerFilter,
  matchesFilter,
  noFilter,
} from "../cuttingLines/CuttingPickerDialog";
import cx from "../ui/cx";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { closeAfterQty, lineName } from "../../static/cuttingLine";
import { formatGujaratiDateTime, formatQty } from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { route } from "../../static/route";
import { CuttingRateSource } from "../../types/cuttingOrder";
import { ICuttingReceiveLineInput } from "../../types/cuttingReceive";

// One card per line of the party's orders that the receive can take: what the line copied when it was ordered, and
// how much of it came on other receives. The form works them out; the picker only shows and picks them.
export type CuttingReceiveCardProps = {
  // The order line it receives.
  lineId: number;
  // The item's category (0 without one): the form opens the picker on one category's cards.
  categoryId: number;
  itemId: number;
  code: string;
  sizeName: string | null;
  // The parent's code for a sub item, else the item's description.
  subtitle: string | null;
  // The page whose item holds it: a sub item is edited on its parent's page.
  editItemId: number;
  imageUrl: string | null;
  materialName: string | null;
  cutLength: number;
  // Only for a user who may see rates.
  rate?: number;
  rateSource?: CuttingRateSource;
  orderId: number;
  orderDate: string;
  qty: number;
  received: number;
  closed: boolean;
};

// The reference card's checkbox: navy border, navy with a white tick when ticked.
const checkboxClass =
  "peer h-4 w-4 cursor-pointer appearance-none rounded border border-[#113F67] bg-transparent checked:bg-[#113F67] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-1";

const orderPath = (orderId: number) =>
  route.cuttingOrderDetail.replace(":id", orderId as unknown as string);

type LinePatch = Partial<
  Pick<ICuttingReceiveLineInput, "qty" | "rate" | "close_order">
>;

// The card's order, with a link to it, and how much of its line has come so far, as on the material purchase picker's
// order cards (whose spacing, the 3px too, was matched to the user's reference card).
const OrderLineInfo = ({
  card,
  canOpenOrder,
}: {
  card: CuttingReceiveCardProps;
  canOpenOrder: boolean;
}) => {
  const progress =
    card.qty > 0 ? Math.min(100, (card.received / card.qty) * 100) : 0;

  return (
    <>
      <div className="mt-[3px] flex items-center gap-1 leading-6 text-[#113F67]">
        <span className="truncate">
          <span className="text-sm font-semibold">ઓર્ડર #{card.orderId}</span>{" "}
          <span className="text-xs text-gray-500">
            ({formatGujaratiDateTime(card.orderDate)})
          </span>
        </span>
        {canOpenOrder && (
          <a
            href={orderPath(card.orderId)}
            target="_blank"
            rel="noopener noreferrer"
            title="ઓર્ડર જુઓ"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="shrink-0 hover:opacity-70"
          >
            <LuExternalLink className="h-[15px] w-[15px]" />
          </a>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-green-600"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="shrink-0 text-xs leading-3 text-gray-500">
          {formatQty(card.received)}/{formatQty(card.qty)} મળેલ
          {card.closed && " • બંધ"}
        </span>
      </div>
    </>
  );
};

type CardProps = {
  card: CuttingReceiveCardProps;
  // The card's line on the picker, once it has a qty.
  line: ICuttingReceiveLineInput | undefined;
  withRates: boolean;
  sizeUnit: string;
  canOpenItem: boolean;
  canOpenOrder: boolean;
  onAddLine: (
    card: CuttingReceiveCardProps,
    qty: string,
    rate: string,
    closeOrder: boolean
  ) => void;
  onRemoveLine: (lineId: number) => void;
  onChangeLine: (lineId: number, patch: LinePatch) => void;
};

// One card per order line, like the order picker's card. Ticking it opens its editor (for a user who may see rates, a
// rate box that starts at the order line's rate); the line goes on the receive once it has a qty, and comes off when its
// qty is cleared. A qty that makes up the order line ticks "ઓર્ડર બંધ". Gets only its own line, so typing in one card
// doesn't redraw the others.
const ReceiveCard = React.memo(
  ({
    card,
    line,
    withRates,
    sizeUnit,
    canOpenItem,
    canOpenOrder,
    onAddLine,
    onRemoveLine,
    onChangeLine,
  }: CardProps) => {
    const isSelected = !!line;
    const name = `${lineName(card.code, card.sizeName)} ઓર્ડર #${card.orderId}`;

    // An open card shows its editor: one with a line opens with it, and ticking opens one.
    const [isOpen, setIsOpen] = useState(isSelected);
    // A rate typed before the qty, and "ઓર્ડર બંધ" ticked before it; both are kept when the qty is cleared.
    const [rateDraft, setRateDraft] = useState<string | null>(null);
    const [closeDraft, setCloseDraft] = useState(false);

    const showEditor = isOpen || isSelected;
    const rate = line ? line.rate : rateDraft ?? rateText(card.rate);

    const qtyRef = useRef<HTMLInputElement>(null);
    // Set when the user opens the card, so the qty box takes the focus once it is there. Cards open when the picker
    // opens leave the focus alone.
    const focusQty = useRef(false);

    useEffect(() => {
      if (focusQty.current && qtyRef.current) {
        focusQty.current = false;
        qtyRef.current.focus();
      }
    });

    const handleToggle = () => {
      if (showEditor) {
        if (line) onRemoveLine(card.lineId);
        setIsOpen(false);
        setRateDraft(null);
        setCloseDraft(false);
      } else {
        focusQty.current = true;
        setIsOpen(true);
      }
    };

    // ઓર્ડર બંધ once the qty goes from previous to qty.
    const closeWith = (previous: string, qty: string, closeOrder: boolean) =>
      closeAfterQty(card.qty, card.received, previous, qty, closeOrder);

    // The first qty puts the line on the receive, at the rate in the box; clearing it takes the line off again.
    const handleQty = (qty: string) => {
      if (!line) {
        if (qty !== "") {
          onAddLine(card, qty, rate, closeWith("", qty, closeDraft));
        }
      } else if (qty === "") {
        setRateDraft(line.rate);
        setCloseDraft(closeWith(line.qty, "", line.close_order));
        onRemoveLine(card.lineId);
      } else {
        onChangeLine(card.lineId, {
          qty,
          close_order: closeWith(line.qty, qty, line.close_order),
        });
      }
    };

    const handleRate = (value: string) => {
      if (line) {
        onChangeLine(card.lineId, { rate: value });
      } else {
        setRateDraft(value);
      }
    };

    const handleCloseOrder = (checked: boolean) => {
      if (line) {
        onChangeLine(card.lineId, { close_order: checked });
      } else {
        setCloseDraft(checked);
      }
    };

    return (
      <CuttingPickerCard
        code={card.code}
        sizeName={card.sizeName}
        subtitle={card.subtitle}
        imageUrl={card.imageUrl}
        editItemId={card.editItemId}
        canOpenItem={canOpenItem}
        materialName={card.materialName}
        cutLength={card.cutLength}
        lengthMissing={false}
        sizeUnit={sizeUnit}
        rate={withRates ? card.rate : undefined}
        rateSource={card.rateSource}
        extra={<OrderLineInfo card={card} canOpenOrder={canOpenOrder} />}
        isOpen={showEditor}
        canToggle
        onToggle={handleToggle}
        editor={
          showEditor && (
            <>
              {withRates && (
                <CardRateRow
                  label={`${name} ભાવ`}
                  value={rate}
                  onChange={handleRate}
                />
              )}

              <CardQtyRow
                label={`${name} જથ્થો`}
                value={line?.qty ?? ""}
                inputRef={qtyRef}
                onChange={handleQty}
              />

              <label className={cx(rowClass, "h-6 cursor-pointer")}>
                <span className={rowLabelClass}>ઓર્ડર બંધ:</span>
                <span className="relative flex h-4 w-4 shrink-0">
                  <input
                    type="checkbox"
                    aria-label={`${name} બંધ`}
                    checked={line ? line.close_order : closeDraft}
                    onChange={(event) => handleCloseOrder(event.target.checked)}
                    className={checkboxClass}
                  />
                  <LuCheck
                    className="pointer-events-none absolute inset-0 m-auto hidden h-3 w-3 text-white peer-checked:block"
                    strokeWidth={3}
                  />
                </span>
              </label>
            </>
          )
        }
      />
    );
  }
);

ReceiveCard.displayName = "ReceiveCard";

type Props = {
  cards: CuttingReceiveCardProps[];
  cardsLoading: boolean;
  // The category the cards belong to, after the title.
  categoryLabel?: string;
  // The form's lines when the picker opens; the picker hands its lines back when it closes.
  initialLines: ICuttingReceiveLineInput[];
  // Rates show only for a user who may see them.
  withRates: boolean;
  sizeUnit: string;
  onClose: (lines: ICuttingReceiveLineInput[]) => void;
};

// Picks the order lines a receive takes, in the order picker's dialog: one card per line, and a receive line per
// card given a qty. The form opens it on one category's cards; the lines of the receive's other categories pass through
// untouched. Mount it only while it is open.
const CuttingReceivePickerModal = ({
  cards,
  cardsLoading,
  categoryLabel,
  initialLines,
  withRates,
  sizeUnit,
  onClose,
}: Props) => {
  const loggingUser = useAppSelector(getLoggingUser);
  const canOpenItem = hasPermission(
    loggingUser?.permissions,
    permission.itemsUpdate
  );
  const canOpenOrder = hasPermission(
    loggingUser?.permissions,
    permission.cuttingOrdersRead
  );

  const [lines, setLines] = useState<ICuttingReceiveLineInput[]>(
    () => initialLines
  );
  // The filter in use: it changes only on "ફિલ્ટર લાગુ કરો" (or Enter).
  const [filter, setFilter] = useState<PickerFilter>(noFilter);

  // The lines already on the form come first. They keep their place while picking, so no card moves under the pointer.
  const [openedWith] = useState(
    () => new Set(initialLines.map((line) => line.order_item_id))
  );

  const lineById = useMemo(
    () => new Map(lines.map((line) => [line.order_item_id, line])),
    [lines]
  );

  // The item of each card's line; another category's lines are other items.
  const itemIdByLine = useMemo(
    () => new Map(cards.map((card) => [card.lineId, card.itemId])),
    [cards]
  );

  // A new line comes right after the same item's other lines, so an item's orders stay together on the form.
  const handleAddLine = useCallback(
    (
      card: CuttingReceiveCardProps,
      qty: string,
      rate: string,
      closeOrder: boolean
    ) => {
      setLines((current) => {
        if (current.some((item) => item.order_item_id === card.lineId)) {
          return current;
        }

        const line: ICuttingReceiveLineInput = {
          order_item_id: card.lineId,
          qty,
          rate,
          close_order: closeOrder,
        };
        const last = current
          .map((item) => itemIdByLine.get(item.order_item_id))
          .lastIndexOf(card.itemId);

        return last === -1
          ? [...current, line]
          : [...current.slice(0, last + 1), line, ...current.slice(last + 1)];
      });
    },
    [itemIdByLine]
  );

  const handleRemoveLine = useCallback((lineId: number) => {
    setLines((current) =>
      current.filter((line) => line.order_item_id !== lineId)
    );
  }, []);

  const handleChangeLine = useCallback((lineId: number, patch: LinePatch) => {
    setLines((current) =>
      current.map((line) =>
        line.order_item_id === lineId ? { ...line, ...patch } : line
      )
    );
  }, []);

  // The cards already on the form first, then the rest, oldest order first, as the filter finds them; the search also
  // finds a card by its order ("#12").
  const visibleCards = useMemo(() => {
    const found = cards.filter((card) =>
      matchesFilter(filter, card.itemId, card.code, [`#${card.orderId}`])
    );

    return [
      ...found.filter((card) => openedWith.has(card.lineId)),
      ...found.filter((card) => !openedWith.has(card.lineId)),
    ];
  }, [cards, openedWith, filter]);

  // The lines of the cards shown: the receive's other categories don't count here.
  const pickedCount = useMemo(
    () => lines.filter((line) => itemIdByLine.has(line.order_item_id)).length,
    [itemIdByLine, lines]
  );

  const handleClose = () => onClose(lines);

  return (
    <CuttingPickerDialog
      categoryLabel={categoryLabel}
      onFilter={setFilter}
      isLoading={cardsLoading && cards.length === 0}
      emptyLabel={
        visibleCards.length > 0
          ? null
          : cards.length === 0
          ? "આ કેટેગરી ની કોઈ બાકી આઈટમ નથી"
          : "ફિલ્ટર મુજબ કોઈ આઈટમ મળી નથી"
      }
      pickedCount={pickedCount}
      onClose={handleClose}
    >
      {visibleCards.map((card) => (
        <ReceiveCard
          key={card.lineId}
          card={card}
          line={lineById.get(card.lineId)}
          withRates={withRates}
          sizeUnit={sizeUnit}
          canOpenItem={canOpenItem}
          canOpenOrder={canOpenOrder}
          onAddLine={handleAddLine}
          onRemoveLine={handleRemoveLine}
          onChangeLine={handleChangeLine}
        />
      ))}
    </CuttingPickerDialog>
  );
};

export default CuttingReceivePickerModal;
